import React, { useEffect, useState } from "react";
import AppPage from "../AppPage";
import { globals, images } from "../../styles";
import { arrow_Icon, eye_Icon, eyeClosed_Icon } from "../../assets/icons";
import { useLogin } from "../../hooks/auth";
import { ApiError } from "../../api";
import { LoginCredentialErrorCode } from "../../api/errorCodes";
import styles from "./LoginPage.module.css";
import { AuthResponse } from "../../api/types";

enum FormField {
  Email = "email",
  Password = "password",
}

function LoginPage(props: {
  handleLoginSuccess: (data: AuthResponse) => void;
  handleBackButton: () => void;
}) {
  const { handleLoginSuccess, handleBackButton } = props;
  const [showPassword, setShowPassword] = useState(false);
  const { mutate: loginUser, isPending, isError, error } = useLogin();
  const [credentialError, setCredentialError] = useState<{
    field: "email" | "password";
    message: string;
  } | null>(null);

  if (
    isError &&
    error instanceof ApiError &&
    [0, 400, 500].includes(error.status)
  ) {
    throw error;
  }

  if (isError && !(error instanceof ApiError)) {
    throw error;
  }

  useEffect(() => {
    if (
      !isError ||
      !(error instanceof ApiError) ||
      (error.status !== 401 && error.status !== 403)
    ) {
      return;
    }

    if (error.status === 403) {
      // TODO: Handle accounts being locked out
      return;
    }

    if (error.code === LoginCredentialErrorCode.IncorrectEmail) {
      setCredentialError({
        field: "email",
        message: "This email does not exist",
      });
    } else if (error.code === LoginCredentialErrorCode.IncorrectPassword) {
      setCredentialError({
        field: "password",
        message: "You have entered the incorrect password",
      });
    }
  }, [isError, error]);

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPending) return;

    const formData = new FormData(e.currentTarget);
    const email = formData.get(FormField.Email) as string;
    const password = formData.get(FormField.Password) as string;

    loginUser(
      { email, password },
      {
        onSuccess: handleLoginSuccess,
      },
    );
  };
  return (
    <AppPage className={globals["page"]}>
      <button
        className={`${globals["button-wrapper"]} ${globals["back-btn--top-left"]}`}
        onClick={handleBackButton}
      >
        <div className={globals["back-btn"]}>
          <img src={arrow_Icon} alt="" className={images["back-arrow-icon"]} />
        </div>
      </button>
      <div className={globals["page-scroll-wrapper"]}>
        <div className={globals["page-container"]}>
          <p className={globals["signing-heading"]}>
            Welcome <br />
            Back!
          </p>
          <p className={globals["signing-subheading"]}>
            Let's get you back in the game.
          </p>
          <form className={globals["user-form"]} onSubmit={handleLogin}>
            <div className={globals["user-form__group"]}>
              <label htmlFor={FormField.Email}>Email</label>
              <input
                type="text"
                id={FormField.Email}
                name={FormField.Email}
                required
                autoComplete="email"
                onChange={() => {
                  if (credentialError?.field === "email")
                    setCredentialError(null);
                }}
              />
            </div>
            {credentialError?.field === "email" && (
              <p className={globals["user-form__error"]}>
                {credentialError.message}
              </p>
            )}
            <div className={globals["user-form__group"]}>
              <label htmlFor={FormField.Password}>Password</label>
              <div className={globals["user-form__input-wrapper"]}>
                <input
                  type={showPassword ? "text" : "password"}
                  id={FormField.Password}
                  name={FormField.Password}
                  required
                  autoComplete="current-password"
                  onChange={() => {
                    if (credentialError?.field === "email")
                      setCredentialError(null);
                  }}
                />
                <button
                  className={`${globals["button-wrapper"]} ${globals["eye-btn"]}`}
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <img
                    src={showPassword ? eyeClosed_Icon : eye_Icon}
                    alt=""
                    className={images["eye-icon"]}
                  />
                </button>
              </div>
              {credentialError?.field === "password" && (
                <p className={globals["user-form__error"]}>
                  {credentialError.message}
                </p>
              )}
            </div>
            <div
              className={`${globals["user-form__submission"]}, ${styles["login-button"]}`}
            >
              <button
                className={globals["button-wrapper"]}
                type="submit"
                disabled={isPending}
              >
                <div className={globals["lrg-btn"]}>Login</div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppPage>
  );
}

export default LoginPage;
